import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { useViewerHasPermission } from "@/auth/shared";
import { Alert } from "@/components/base/alert";
import { title } from "@/components/base/primitives";
import { TshirtSizeDisplay } from "@/components/tournaments/tshirt-size-display";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { playerName } from "@/utils/profiles";

export const Route = createFileRoute("/tshirt-pickup/$membershipId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { membershipId } = Route.useParams();

  const id = Number.parseInt(membershipId, 10);
  const trpc = useTRPC();
  const isTd = useViewerHasPermission({ tournaments: ["update"] });
  const queryClient = useQueryClient();

  const queryOptions = trpc.tournaments.getTshirtPickup.queryOptions({
    membershipId: id,
  });
  const { data, isLoading, error } = useQuery({
    ...queryOptions,
    enabled: !Number.isNaN(id),
  });

  const { mutate, isPending } = useMutation({
    ...trpc.tournaments.markTshirtPickedUp.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(queryOptions);
    },
  });

  if (Number.isNaN(id)) {
    return (
      <DefaultLayout>
        <div className="mx-auto max-w-md px-3 py-12">
          <Alert
            color="error"
            title="Not Found"
            description="This membership was not found or has been revoked."
          />
        </div>
      </DefaultLayout>
    );
  }

  if (isLoading) {
    return (
      <DefaultLayout>
        <div className="mx-auto max-w-md px-3 py-12 text-center">
          Loading...
        </div>
      </DefaultLayout>
    );
  }

  if (error || !data) {
    return (
      <DefaultLayout>
        <div className="mx-auto max-w-md px-3 py-12">
          <Alert
            color="error"
            title="Not Found"
            description="This membership was not found or has been revoked."
          />
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="mx-auto flex max-w-md flex-col items-center gap-6 px-3 py-12">
        <h1 className={title()}>T-Shirt Pickup</h1>

        <div className="space-y-6 text-center">
          <div className="text-xl font-semibold">
            {playerName(data.profile)}
          </div>
          <TshirtSizeDisplay
            membershipId={id}
            tshirtSize={data.tshirtSize}
            tshirtPickedUpAt={data.tshirtPickedUpAt}
            onUpdated={() => queryClient.invalidateQueries(queryOptions)}
            onTogglePickup={() => mutate({ membershipId: id })}
            isTogglingPickup={isPending}
            isTd={isTd}
          />
        </div>
      </div>
    </DefaultLayout>
  );
}
